import { Link, useLocation } from "react-router-dom";
import * as OutlineIcons from "@/icons/outline";
import * as SolidIcons from "@/icons/solid";
import { useMemo } from "react";
import { useState } from "react";
import { useEffect } from "react";
import { useLogValue } from "@/helpers/ReactHooks";
import Tooltip from "@/tooltip";

export default function LayoutSidebar({ defaultPath, children, sections = [], conditions }) {
	return (
		<div className="border-r-2 border-solid border-gray-100 h-[calc(100%-153.59px)] overflow-y-auto overflow-x-none w-full pb-12">
			<ul className="gap-y-2 grid pr-8 pt-8 w-full">
				{sections.map((tab) => {
					const section = !!tab.tabs;
					const tabs = tab.tabs || [tab];

					return (
						<>
							{section && (
								<SidebarTitle
									tab={tab}
									key={tab.name}
								/>
							)}
							{tabs.map((item) => {
								const folder = !!item.subTabs;
								const TabComponent = folder ? SidebarFolder : SidebarItem;

								return (
									<TabComponent
										{...{
											key: item.link,
											tab: item,
											defaultPath,
											conditions,
										}}
									/>
								);
							})}
						</>
					);
				})}
				{children}
			</ul>
		</div>
	);
}

export function SidebarTitle({ tab, children }) {
	return (
		<li className="text-sm font-semibold text-gray-500 uppercase px-4 xl:text-left text-center">
			{tab.name}
			{children}
		</li>
	);
}

const SIDEBAR_ITEM_STYLES = {
	active: "bg-gray-100 xl:pl-5 px-3 py-2 text-2xl text-gray-900 cursor-pointer",
	normal: "px-3 py-2 text-2xl hover:bg-gray-50 text-primary-500 cursor-pointer",
	disabled: "px-3 py-2 text-2xl text-gray-400 cursor-not-allowed hover:text-gray-400 focus:text-gray-400",
};

export function SidebarItem({ tab, defaultPath, conditions, children }) {
	const location = useLocation();
	const currentTab = location.pathname;
	const paths = (defaultPath + tab.link).split("/");
	const tabPaths = currentTab.split("/");

	const matchingPaths = paths.filter((p, i) => p === tabPaths[i])?.length;
	const disabledTab = !tab.component || tab.disabled;

	const active = useMemo(
		() => (paths?.length === tabPaths.length ? paths.length / matchingPaths <= 1 : false),
		[tab.link, paths?.length, matchingPaths, currentTab],
	);
	const className = useMemo(() => {
		if (disabledTab) return SIDEBAR_ITEM_STYLES.disabled;
		if (active) return SIDEBAR_ITEM_STYLES.active;
		return SIDEBAR_ITEM_STYLES.normal;
	}, [disabledTab, currentTab, active]);

	const Icon = !active ? OutlineIcons[tab.icon] : SolidIcons[tab.icon];
	const Tab = disabledTab ? "span" : Link;

	const show = tab?.show?.(conditions);

	if (typeof tab?.show !== "undefined" && !show) return null;
	return (
		<li className="w-full">
			<Tab
				to={disabledTab ? undefined : defaultPath + tab.link}
				disabled={!tab.component}
				className={`w-full list-item rounded-lg font-medium select-none relative ${className}`}
				onClick={(e) => {
					if (disabledTab) {
						e.preventDefault();
						e.stopPropagation();
					}
				}}
			>
				<Tooltip
					contentClassName={`${!Icon ? "xl:hidden " : ""}inline-block text-2xl`}
					forceDirection
					direction="right"
					arrow={false}
					arrowClassName={`${!Icon ? "xl:hidden " : ""}inline-block`}
					content={tab?.name}
					distance={30}
					className="w-min xl:w-full mx-auto xl:grid xl:grid-cols-4"
				>
					<span className="xl:w-[calc(100% - 32px)] overflow-clip text-ellipsis text-nowrap col-span-4">
						{Icon ? <Icon className="h-8 w-8 xl:mr-3 align-middle" /> : null}
						<span className={`${Icon ? "hidden " : ""}xl:inline`}>{tab?.name || null}</span>
					</span>
				</Tooltip>
				{children}
			</Tab>
		</li>
	);
}

export function SidebarFolder({ tab, defaultPath, conditions, children }) {
	const location = useLocation();
	const currentTab = location.pathname.split("#")[0];
	const paths = useLogValue(tab.link?.split?.("/") || []);
	const tabPaths = useLogValue(currentTab.replace(defaultPath, "").split("/"));

	const matchingPaths = tabPaths.filter((p, i) => p === paths[i])?.length;
	const disabledTab = !tab.component || tab.disabled;

	const [expand, setExpand] = useState(false);

	const active = useMemo(() => {
		if (paths.length > tabPaths.length) return false;
		return paths.length / matchingPaths <= 1;
	}, [paths?.length, matchingPaths, currentTab]);

	const className = useMemo(() => {
		if (disabledTab) return SIDEBAR_ITEM_STYLES.disabled;
		if (active || expand) return SIDEBAR_ITEM_STYLES.active;
		return SIDEBAR_ITEM_STYLES.normal;
	}, [disabledTab, currentTab, active, expand, JSON.stringify(conditions)]);

	const Icon = !active && !expand ? OutlineIcons[tab.icon] : SolidIcons[tab.icon];

	useEffect(() => {
		setExpand(active);
	}, [currentTab, JSON.stringify(conditions)]);

	const show = tab?.show?.(conditions);

	if (typeof tab?.show !== "undefined" && !show && !active) return null;

	return (
		<li className="w-full">
			<ul className={`${className} rounded-lg`}>
				<li
					disabled={!tab.component}
					className="w-full rounded-lg font-medium select-none mb-2 relative"
					onClick={(e) => {
						if (disabledTab) {
							e.preventDefault();
							e.stopPropagation();
							return;
						}
						setExpand(!expand);
					}}
				>
					<Tooltip
						contentClassName={`${!Icon ? "xl:hidden " : ""}inline-block text-2xl`}
						forceDirection
						direction="right"
						arrow={false}
						arrowClassName={`${!Icon ? "xl:hidden " : ""}inline-block`}
						content={tab?.name}
						distance={30}
						className="w-min xl:w-full mx-auto xl:grid xl:grid-cols-4"
					>
						<span className="xl:w-[calc(100% - 32px)] overflow-clip text-ellipsis text-nowrap col-span-3">
							{Icon ? <Icon className="h-8 w-8 xl:mr-3 align-middle" /> : null}
							<span className={`${Icon ? "hidden " : ""}xl:inline`}>{tab?.name || null}</span>
						</span>
						<SolidIcons.ChevronDownIcon
							className={`h-8 w-8 align-middle transition absolute -top-2 left-6 xl:relative ${
								expand ? "rotate-180" : ""
							}`}
						/>
					</Tooltip>
				</li>
				{expand &&
					tab.subTabs.map((item) => {
						return (
							<SidebarItem
								{...{
									key: item.link,
									tab: item,
									defaultPath,
									conditions,
								}}
							/>
						);
					})}
			</ul>
			{children}
		</li>
	);
}
